import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Theme context
const ThemeContext = createContext();

// Professional Dark Theme Colors (inspired by tour management apps)
const darkThemeColors = {
    primary: {
        main: '#3b82f6',      // Vibrant blue
        light: '#60a5fa',
        dark: '#2563eb',
        contrastText: '#ffffff'
    },
    secondary: {
        main: '#f43f5e',      // Modern rose
        light: '#fb7185',
        dark: '#e11d48',
        contrastText: '#ffffff'
    },
    background: {
        default: '#0c0f14',   // Deep navy-black
        paper: '#161b22',     // Elevated dark surface
        sidebar: '#12161d',   // Slightly darker than paper
        navbar: '#161b22',    // Matches Paper
    },
    surface: {
        level1: '#1c2230',    // Elevated surfaces with blue tint
        level2: '#252d3d',    // Higher elevation
        level3: '#303a4f',    // Highest elevation
    },
    text: {
        primary: '#e6edf3',   // Soft white (less harsh)
        secondary: '#8b949e', // Muted gray
        disabled: '#545d68',  // Disabled text
        hint: '#8b949e',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
    success: {
        main: '#22c55e',      // Vibrant green
        light: '#4ade80',
        dark: '#16a34a',
        contrastText: '#ffffff'
    },
    warning: {
        main: '#eab308',      // Warm yellow
        light: '#facc15',
        dark: '#ca8a04',
        contrastText: '#000000'
    },
    error: {
        main: '#ef4444',      // Vivid red
        light: '#f87171',
        dark: '#dc2626',
        contrastText: '#ffffff'
    },
    info: {
        main: '#3b82f6',      // Matches primary
        light: '#60a5fa',
        dark: '#2563eb',
        contrastText: '#ffffff'
    }
};

// Professional Light Theme Colors
const lightThemeColors = {
    primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
        contrastText: '#ffffff'
    },
    secondary: {
        main: '#f50057',
        light: '#ff5983',
        dark: '#c51162',
        contrastText: '#ffffff'
    },
    background: {
        default: '#f6f8fa',   // Light gray canvas
        paper: '#ffffff',     // pure white cards
        sidebar: '#f6f8fa',   // Blends with bg or slight contrast
        navbar: '#ffffff',
    },
    surface: {
        level1: '#f6f8fa',
        level2: '#eaeef2',
        level3: '#d0d7de',
    },
    text: {
        primary: '#24292f',
        secondary: '#656d76',
        disabled: '#8c959f',
        hint: '#656d76',
    },
    divider: '#d0d7de',
    success: {
        main: '#1a7f37',
        light: '#2da44e',
        dark: '#116329',
        contrastText: '#ffffff'
    },
    warning: {
        main: '#bf8700',
        light: '#d4a72c',
        dark: '#9a6700',
        contrastText: '#000000'
    },
    error: {
        main: '#cf222e',
        light: '#d1242f',
        dark: '#a40e26',
        contrastText: '#ffffff'
    },
    info: {
        main: '#0969da',
        light: '#218bff',
        dark: '#0550ae',
        contrastText: '#ffffff'
    }
};

// Create themes
const createAppTheme = (mode) => {
    const colors = mode === 'dark' ? darkThemeColors : lightThemeColors;

    return createTheme({
        palette: {
            mode,
            ...colors,
            // Custom palette extensions
            sidebar: {
                background: colors.background.sidebar,
                text: colors.text.primary,
                textSecondary: colors.text.secondary,
                icon: colors.text.secondary,
                iconActive: colors.primary.main,
                hover: colors.surface.level1,
                active: colors.surface.level2,
            },
            navbar: {
                background: colors.background.navbar,
                text: colors.text.primary,
                border: colors.divider,
            },
            card: {
                background: colors.background.paper,
                border: colors.divider,
                shadow: mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            }
        },
        typography: {
            fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: {
                fontWeight: 700,
                fontSize: '2.5rem',
                lineHeight: 1.2,
                color: colors.text.primary,
            },
            h2: {
                fontWeight: 600,
                fontSize: '2rem',
                lineHeight: 1.3,
                color: colors.text.primary,
            },
            h3: {
                fontWeight: 600,
                fontSize: '1.75rem',
                lineHeight: 1.3,
                color: colors.text.primary,
            },
            h4: {
                fontWeight: 600,
                fontSize: '1.5rem',
                lineHeight: 1.4,
                color: colors.text.primary,
            },
            h5: {
                fontWeight: 600,
                fontSize: '1.25rem',
                lineHeight: 1.4,
                color: colors.text.primary,
            },
            h6: {
                fontWeight: 600,
                fontSize: '1.125rem',
                lineHeight: 1.4,
                color: colors.text.primary,
            },
            body1: {
                fontSize: '1rem',
                lineHeight: 1.5,
                color: colors.text.primary,
            },
            body2: {
                fontSize: '0.875rem',
                lineHeight: 1.5,
                color: colors.text.secondary,
            },
            caption: {
                fontSize: '0.75rem',
                lineHeight: 1.4,
                color: colors.text.secondary,
            }
        },
        shape: {
            borderRadius: 8,
        },
        shadows: mode === 'dark' ? [
            'none',
            '0px 1px 3px rgba(0,0,0,0.3)',
            '0px 2px 6px rgba(0,0,0,0.3)',
            '0px 3px 12px rgba(0,0,0,0.3)',
            '0px 4px 16px rgba(0,0,0,0.3)',
            '0px 5px 20px rgba(0,0,0,0.3)',
            '0px 6px 24px rgba(0,0,0,0.3)',
            '0px 7px 28px rgba(0,0,0,0.3)',
            '0px 8px 32px rgba(0,0,0,0.3)',
            '0px 9px 36px rgba(0,0,0,0.3)',
            '0px 10px 40px rgba(0,0,0,0.3)',
            '0px 11px 44px rgba(0,0,0,0.3)',
            '0px 12px 48px rgba(0,0,0,0.3)',
            '0px 13px 52px rgba(0,0,0,0.3)',
            '0px 14px 56px rgba(0,0,0,0.3)',
            '0px 15px 60px rgba(0,0,0,0.3)',
            '0px 16px 64px rgba(0,0,0,0.3)',
            '0px 17px 68px rgba(0,0,0,0.3)',
            '0px 18px 72px rgba(0,0,0,0.3)',
            '0px 19px 76px rgba(0,0,0,0.3)',
            '0px 20px 80px rgba(0,0,0,0.3)',
            '0px 21px 84px rgba(0,0,0,0.3)',
            '0px 22px 88px rgba(0,0,0,0.3)',
            '0px 23px 92px rgba(0,0,0,0.3)',
            '0px 24px 96px rgba(0,0,0,0.3)',
        ] : [
            'none',
            '0px 1px 3px rgba(0,0,0,0.12)',
            '0px 2px 6px rgba(0,0,0,0.12)',
            '0px 3px 12px rgba(0,0,0,0.12)',
            '0px 4px 16px rgba(0,0,0,0.12)',
            '0px 5px 20px rgba(0,0,0,0.12)',
            '0px 6px 24px rgba(0,0,0,0.12)',
            '0px 7px 28px rgba(0,0,0,0.12)',
            '0px 8px 32px rgba(0,0,0,0.12)',
            '0px 9px 36px rgba(0,0,0,0.12)',
            '0px 10px 40px rgba(0,0,0,0.12)',
            '0px 11px 44px rgba(0,0,0,0.12)',
            '0px 12px 48px rgba(0,0,0,0.12)',
            '0px 13px 52px rgba(0,0,0,0.12)',
            '0px 14px 56px rgba(0,0,0,0.12)',
            '0px 15px 60px rgba(0,0,0,0.12)',
            '0px 16px 64px rgba(0,0,0,0.12)',
            '0px 17px 68px rgba(0,0,0,0.12)',
            '0px 18px 72px rgba(0,0,0,0.12)',
            '0px 19px 76px rgba(0,0,0,0.12)',
            '0px 20px 80px rgba(0,0,0,0.12)',
            '0px 21px 84px rgba(0,0,0,0.12)',
            '0px 22px 88px rgba(0,0,0,0.12)',
            '0px 23px 92px rgba(0,0,0,0.12)',
            '0px 24px 96px rgba(0,0,0,0.12)',
        ],
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    html: {
                        backgroundColor: colors.background.default,
                    },
                    body: {
                        backgroundColor: colors.background.default,
                        color: colors.text.primary,
                        fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                    },
                    '#root': {
                        backgroundColor: colors.background.default,
                        minHeight: '100vh',
                    },
                    '.app-container': {
                        backgroundColor: colors.background.default,
                        minHeight: '100vh',
                        color: colors.text.primary,
                    },
                    '.main-content': {
                        backgroundColor: colors.background.default,
                        flex: 1, // Ensure it fills space
                    },
                    '*::-webkit-scrollbar': {
                        width: '6px',
                        height: '6px',
                    },
                    '*::-webkit-scrollbar-track': {
                        backgroundColor: 'transparent',
                    },
                    '*::-webkit-scrollbar-thumb': {
                        backgroundColor: colors.surface.level2,
                        borderRadius: '3px',
                        '&:hover': {
                            backgroundColor: colors.surface.level3,
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.background.navbar,
                        color: colors.text.primary,
                        borderBottom: `1px solid ${colors.divider}`,
                        boxShadow: 'none',
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        backgroundColor: colors.background.sidebar,
                        borderRight: `1px solid ${colors.divider}`,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.background.paper,
                        border: `1px solid ${colors.divider}`,
                        borderRadius: '12px',
                        boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.background.paper,
                        color: colors.text.primary,
                        backgroundImage: 'none',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        letterSpacing: '-0.01em',
                    },
                    contained: {
                        boxShadow: 'none',
                        '&:hover': { boxShadow: 'none' },
                    },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.surface.level1,
                        color: colors.text.primary,
                        fontWeight: 600,
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '8px',
                        transition: 'all 0.15s ease',
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                    },
                },
            },
        },
    });
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(() => {
        // Load theme preference from localStorage
        const saved = localStorage.getItem('tourminator-theme');
        return saved || 'dark'; // Default to dark theme
    });

    const theme = createAppTheme(mode);

    const toggleTheme = () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
        localStorage.setItem('tourminator-theme', newMode);
    };

    const setTheme = (newMode) => {
        setMode(newMode);
        localStorage.setItem('tourminator-theme', newMode);
    };

    useEffect(() => {
        // Apply theme to document root for global styles
        document.documentElement.setAttribute('data-theme', mode);
    }, [mode]);

    const value = {
        mode,
        theme,
        toggleTheme,
        setTheme,
        isDark: mode === 'dark',
        isLight: mode === 'light',
    };

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

// Hook to use theme
export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeProvider;